var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Matrix as BABYLON_Matrix, Vector3 as BABYLON_Vector3, Vector4 as BABYLON_Vector4 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { AbstractMesh as BABYLON_AbstractMesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/abstractMesh.js';
import { TransformNode as BABYLON_TransformNode } from '@inter-ikea-betrakta-babylonjs/core/Meshes/transformNode.js';
import { IColor, IVector3 } from '../api.js';
import { ViewerMaterial } from './material/ViewerMaterial.js';
import { MaterialCollection } from './material/MaterialCollection.js';
import { ViewerMaterialWrapper } from './material/ViewerMaterialWrapper.js';
import { Color4 as BABYLON_Color4 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.color.js';
import { Observable } from '../Observable.js';
import { VertexBuffer as BABYLON_VertexBuffer } from '@inter-ikea-betrakta-babylonjs/core/Buffers/buffer.js';
import { mark_mesh_dirty_for_lightsystem } from './MeshManager.js';
import { RENDER_LAYER_SHADOW, RENDER_LAYER_FORWARD, RenderLayerMask } from './RenderLayer.js';
import { assert } from './utility/Assert.js';
import { is_aabb, is_sphere, aabb_center, aabb_size, clone_bounds } from './utility/Bounds.js';
import { Debug } from '../utils/DebugLayer/utils/DebugDecorators.js';
import { CustomMaterialWrapper } from './material/CustomMaterialWrapper.js';
let SceneObject = class SceneObject {
    get mesh_nodes() {
        if (this._asset_instance === null) {
            return [];
        }
        else {
            return this._asset_instance.asset_root.getChildMeshes(false);
        }
    }
    get has_bounds() {
        return this._has_bounds;
    }
    get root_node() {
        return this._root_node;
    }
    get id() {
        return this._object_id;
    }
    get parent_id() {
        const parent = this._parent_object;
        if (parent === null)
            return null;
        return parent.id;
    }
    // This should be handled by a more flexible logging system in the long run
    // but for now we just check this since it is something that several users
    // had issues with.
    verbose_input_warning() {
        if (this.viewer.get_config().verbose_warnings &&
            this.is_empty &&
            (this._interaction_bounds === null || this._interaction_bounds.length === 0)) {
            console.warn('Input handlers for empty object without extra bounds will never trigger');
        }
    }
    get drag_begin_observable() {
        assert(this._input_handler.execution_object === this, 'SceneObject: Invalid execution object in input handler');
        this.verbose_input_warning();
        return this._input_handler.drag_begin_observable;
    }
    get drag_observable() {
        assert(this._input_handler.execution_object === this, 'SceneObject: Invalid execution object in input handler');
        this.verbose_input_warning();
        return this._input_handler.drag_observable;
    }
    get drag_end_observable() {
        assert(this._input_handler.execution_object === this, 'SceneObject: Invalid execution object in input handler');
        this.verbose_input_warning();
        return this._input_handler.drag_end_observable;
    }
    get pointer_enter_observable() {
        assert(this._input_handler.execution_object === this, 'SceneObject: Invalid execution object in input handler');
        this.verbose_input_warning();
        return this._input_handler.pointer_enter_observable;
    }
    get pointer_leave_observable() {
        assert(this._input_handler.execution_object === this, 'SceneObject: Invalid execution object in input handler');
        this.verbose_input_warning();
        return this._input_handler.pointer_leave_observable;
    }
    get is_loaded() {
        return this._is_loaded;
    }
    get click_observable() {
        assert(this._input_handler.execution_object === this, 'SceneObject: Invalid execution object in input handler');
        this.verbose_input_warning();
        return this._input_handler.click_observable;
    }
    get secondary_click_observable() {
        assert(this._input_handler.execution_object === this, 'SceneObject: Invalid execution object in input handler');
        this.verbose_input_warning();
        return this._input_handler.secondary_click_observable;
    }
    get changed_observable() {
        return this._changed_observable;
    }
    get disposed_observable() {
        return this._disposed_observable;
    }
    get child_added_observable() {
        return this._child_added_observable;
    }
    get child_removed_observable() {
        return this._child_removed_observable;
    }
    get show_interaction_bounds() {
        return this._show_interaction_bounds;
    }
    set show_interaction_bounds(value) {
        this._show_interaction_bounds = value;
        if (value && this._interaction_bounds_tint === null) {
            // w is for roughness but we run unlit so we just set it to something
            this._interaction_bounds_tint = { x: Math.random(), y: Math.random(), z: Math.random() };
        }
        this.update_raycast_bounds_visualization();
    }
    constructor(_asset_instance, id, viewer, viewer_scene, is_empty) {
        this.viewer = viewer;
        this.viewer_scene = viewer_scene;
        this.is_empty = is_empty;
        /**
         * @deprecated
         * This will be replaced with new material system
         */
        this.material = null; // This material might not be ready/used yet
        this._override_material = null;
        this._visible = true;
        this._mesh_url = null;
        this._base_color = new BABYLON_Vector4(1, 1, 1, 1);
        this._outline = false;
        this._outline_block = false;
        this._outline_color = null;
        this._outline_width = null;
        this._children_objects = null;
        this._parent_object = null;
        this._receive_shadow = true;
        // Crappy solution for cases where we create custom babylon meshes (MeshManager.create_custom_mesh), and need to be able to replace them (reuse_object)
        // Tabletop solution depends on this.
        this.babylon_custom_mesh_root = null;
        // Bounding volumes in local space (do NOT update with world matrix), that doesn't include child-objects.
        // Will be null when we can't cache the bounds, in case we have a skeletal mesh
        this._local_bounding_info = null;
        this._local_bounding_info_calculation_promise = null;
        // To note if our meshes (not including child objects) contain skeletal animations
        this._has_skeletal_animation = false;
        // Number.MAX_VALUE is the default for alpha_index in BabylonJS https://doc.babylonjs.com/divingDeeper/materials/advanced/transparent_rendering#alpha-index
        // We have chosen to change this to 10000 instead to make it easier to move objects both forward and backwards in the queue
        this._alpha_index = 10000;
        this._on_drag_enable = true;
        this._on_click_enable = true;
        this.ready = Promise.resolve();
        this._interaction_bounds = null;
        this._is_loaded = false;
        this._changed_observable = new Observable();
        this._disposed_observable = new Observable();
        this._child_added_observable = new Observable();
        this._child_removed_observable = new Observable();
        this._asset_instance_animations = [];
        // TODO: Remove with new asset manager
        this._has_bounds = false;
        this._interaction_bounds_tint = null;
        this._show_interaction_bounds = false;
        this.bounds_meshes = [];
        this._root_node = new BABYLON_TransformNode('', viewer.scene); // Need to provide scene, after exporting a gltf the default scene reference is null. The planner camera should always be in the main scene.
        this._changed_observable.add(() => {
            this.viewer_scene.object_changed_observable.notify(this.viewer_scene, this);
        });
        this._asset_instance = _asset_instance;
        if (this._asset_instance !== null) {
            this._asset_instance.set_parent(this._root_node);
            this._asset_instance.meshes_updated_observable.add(() => {
                this.on_mesh_update();
            });
        }
        this._object_id = id;
        this._render_layer_mask = new RenderLayerMask([RENDER_LAYER_FORWARD]);
        this._input_handler = viewer.create_input_handler(this, [this]);
        if (this._asset_instance) {
            const loaded_promise = this._asset_instance.asset.get_loaded_promise();
            loaded_promise.then(() => {
                this._is_loaded = true;
                this.on_mesh_update();
            }, () => {
                // Do nothing? But we need the catch otherwise we get errors in the console when failing to load.
            });
        }
    }
    on_mesh_update() {
        var _a, _b, _c;
        // Update mesh properties
        this.mesh_iterator(false, mesh => {
            mesh.overlayAlpha = 0.5;
            mesh.isPickable = false;
            mesh.isVisible = this._visible;
            mesh.alphaIndex = this._alpha_index;
            if (mesh.skeleton !== null) {
                this._has_skeletal_animation = true;
            }
        });
        this._update_meshes_render_mask();
        this.update_albedo_roughness_factor_internal();
        this.apply_outline_state();
        // TODO: Investigate how to get rid of this and put it in the asset system.
        this.mark_world_matrices_to_be_recomputed();
        this.recalculate_local_bounds();
        this._has_bounds = true;
        this.viewer.mark_world_bounds_dirty();
        (_a = this._asset_instance) === null || _a === void 0 ? void 0 : _a.instance_iterator((instance) => {
            instance.rendering_material_changed_observable.add(() => {
                this._changed_observable.notify(this, this);
            });
        });
        // Delete old animations coming from our asset
        if (this._asset_instance_animations.length > 0) {
            this.viewer.animation_system.delete_animations(this.id, this._asset_instance_animations);
            this._asset_instance_animations.length = 0;
        }
        // Load new animations from our asset
        (_c = (_b = this._asset_instance) === null || _b === void 0 ? void 0 : _b.animation_groups) === null || _c === void 0 ? void 0 : _c.forEach(animation => {
            this._asset_instance_animations.push(this.viewer.animation_system.register_animation(this.id, animation));
        });
        this._changed_observable.notify(this, this);
    }
    add_render_layer(layer) {
        this.set_render_layer_mask(RenderLayerMask.add_layer(this._render_layer_mask, layer));
    }
    remove_render_layer(layer) {
        this.set_render_layer_mask(RenderLayerMask.remove_layer(this._render_layer_mask, layer));
    }
    get_render_layer_mask() {
        return this._render_layer_mask;
    }
    set_render_layer_mask(mask) {
        const update_meshes = mask.bitmap !== this._render_layer_mask.bitmap;
        const new_mask_has_shadow_caster = RenderLayerMask.has_layer(mask, RENDER_LAYER_SHADOW);
        const old_mask_has_shadow_caster = RenderLayerMask.has_layer(this._render_layer_mask, RENDER_LAYER_SHADOW);
        this._render_layer_mask = RenderLayerMask.clone(mask);
        if (new_mask_has_shadow_caster !== old_mask_has_shadow_caster) {
            // We have to update shadow state
            this.viewer.light_system.set_cast_shadow(this, new_mask_has_shadow_caster);
            this.viewer.freezing_manager.set_all_materials_dirty();
            this.viewer.mark_scene_dirty();
        }
        if (update_meshes) {
            this._update_meshes_render_mask();
        }
    }
    has_render_layer(layer) {
        return RenderLayerMask.has_layer(this._render_layer_mask, layer);
    }
    _update_meshes_render_mask() {
        this.mesh_iterator(false, (mesh) => {
            mesh.layerMask = this._render_layer_mask.bitmap;
        });
    }
    /**
     * @deprecated Old material system
     */
    set_material(id) {
        return __awaiter(this, void 0, void 0, function* () {
            this.material = id;
            if (this._asset_instance === null)
                return;
            if (id === null) {
                this._asset_instance.set_override_material(null);
            }
            else {
                const material = this.viewer.asset_manager.materials.get(id);
                if (material.material !== null) {
                    this._asset_instance.set_override_material(material.material);
                }
                else {
                    yield material.ready;
                    if (this.material === id) {
                        // Only apply the material after awaiting if no one else as set a different material in the meantime
                        this._asset_instance.set_override_material(material.material);
                    }
                }
            }
        });
    }
    /**
     * @deprecated Old material system
     */
    get_material() {
        return this.material;
    }
    /**
     * New material system
     * Assignes an override material to all meshes of this scene object
     * @param material override material
     */
    set_override_material(material) {
        var _a, _b, _c;
        if (!this.viewer.get_optional_features().feature_gate_material_objects)
            throw Error('material objects feature gate is not enabled');
        if (material !== null && !(material instanceof ViewerMaterialWrapper || material instanceof CustomMaterialWrapper))
            throw Error('override material is of wrong backing type');
        this._override_material = material;
        (_a = this._asset_instance) === null || _a === void 0 ? void 0 : _a.set_override_material((_c = (_b = this._override_material) === null || _b === void 0 ? void 0 : _b.material) !== null && _c !== void 0 ? _c : null);
    }
    /**
     * This endpoint always returns the currently assigned materials to this model.
     * For models loaded from glTF this can be the material that is also applied to all future loaded versions of this model
     * This can be checked using the {@link IMaterial.is_applied_by_model_loader} on IMaterial
     * @returns A collection of all materials currently assigned to this model
     */
    get_shared_materials() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.viewer.get_optional_features().feature_gate_material_objects) {
                throw new Error('material objects feature gate is not enabled');
            }
            if (this._asset_instance === null)
                return new MaterialCollection([]);
            yield this._asset_instance.asset.get_loaded_promise();
            const materials = this.get_all_materials();
            if (materials.size === 0) {
                throw Error('could not find any materials');
            }
            else {
                return new MaterialCollection(materials);
            }
        });
    }
    /**
     * This endpoint always returns a collection of materials where this model is guaranteed to be the only user of each material when the promise resolves
     * @returns A collection of all materials assigned to this model, each material is only assigned to this model
     */
    get_unique_materials() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.viewer.get_optional_features().feature_gate_material_objects) {
                throw new Error('material objects feature gate is not enabled');
            }
            if (this._asset_instance === null)
                return new MaterialCollection([]);
            yield this._asset_instance.asset.get_loaded_promise();
            const materials = this.get_all_materials();
            if (materials.size === 0) {
                throw Error('could not find any materials');
            }
            else {
                // TODO: Here we only want to copy materials if they aren't unique?
                const new_materials = new Set();
                materials.forEach((material) => {
                    const material_copy = material.clone();
                    new_materials.add(material_copy);
                    this.replace_material(material, material_copy);
                });
                return new MaterialCollection(new_materials);
            }
        });
    }
    get_all_materials() {
        const materials = new Set();
        if (this._asset_instance === null)
            return materials;
        this._asset_instance.instance_iterator((instance) => {
            const material = instance.get_active_material();
            // Objects with our default material assigned are considered to not have materials
            if (material === this.viewer.asset_manager.default_material)
                return;
            if (material === null) {
                console.error(`Mesh: ${instance.babylon_instance.name} has a null material`);
                throw Error('null material on one or more meshes');
            }
            else if (!(material instanceof ViewerMaterial)) {
                console.error(`Mesh: ${instance.babylon_instance.name} has a none viewer material`);
                throw Error('bad material type on one or more meshes');
            }
            materials.add(this.viewer.asset_manager.get_or_create_material_wrapper(material));
        });
        return materials;
    }
    // Note that you need to make sure that the object has finished loading before using this function for it to work
    // This function will just fail if the object has no mesh
    replace_material(current_material, new_material) {
        if (!this.viewer.get_optional_features().feature_gate_material_objects) {
            throw new Error('material objects feature gate is not enabled');
        }
        if (!(current_material instanceof ViewerMaterialWrapper) && !(current_material instanceof CustomMaterialWrapper)) {
            throw new Error('current_material backing object is of the wrong class');
        }
        if (!(new_material instanceof ViewerMaterialWrapper) && !(new_material instanceof CustomMaterialWrapper)) {
            throw new Error('current_material backing object is of the wrong class');
        }
        if (this._asset_instance === null)
            return false;
        let replaced_material = false;
        this._asset_instance.instance_iterator((instance) => {
            const custom_material = instance.get_active_material();
            // Check if we want to swap the material for this mesh
            if (custom_material === current_material.material) {
                instance.set_custom_material(new_material.material);
                replaced_material = true;
            }
        });
        return replaced_material;
    }
    apply_outline_state() {
        this.viewer.outline_layer.set_outline(this, this._outline);
        this.viewer.outline_layer.set_outline_block(this, this._outline_block);
        if (this._outline) {
            // Only necessary to set color and width if visible
            // TODO: See IC3V-1569 with regards to applying outline properties on non-outline objects
            this.viewer.outline_layer.set_object_outline_color(this, this._outline_color);
            this.viewer.outline_layer.set_object_outline_width(this, this._outline_width);
        }
    }
    // Iterate all mesh nodes in this object (recurse === false) or in all this object and all children objects (recurse===true)
    mesh_iterator(recurse, func) {
        this.children_iterator(recurse, true, (object) => {
            var _a;
            (_a = this._asset_instance) === null || _a === void 0 ? void 0 : _a.mesh_iterator(func);
        });
    }
    // Iterate the full hierarchy of objects. Note that these are not babylon scene object but rather SceneObject that can each wrap a small babylon graph of babylon nodes
    children_iterator(recurse, include_self, func) {
        if (include_self) {
            func(this);
        }
        if (recurse && this._children_objects) {
            this._children_objects.forEach(child => {
                child.children_iterator(recurse, true, func);
            });
        }
    }
    children_direct_descendants_iterator(func) {
        if (this._children_objects) {
            this._children_objects.forEach(child => {
                func(child);
            });
        }
    }
    replace(old_object) {
        const new_parent = old_object.parent;
        old_object.set_parent(null);
        // TODO: Do we want to move over any other attribute? All of them? None?
        const old_position = old_object.get_position();
        this.set_position(old_position.x, old_position.y, old_position.z);
        const old_scale = old_object.get_scale();
        this.set_scale(old_scale.x, old_scale.y, old_scale.z);
        const old_rotation = old_object.get_rotation();
        this.set_rotation(old_rotation.x, old_rotation.y, old_rotation.z);
        this._children_objects = old_object._children_objects;
        old_object._children_objects = null;
        if (this._children_objects) {
            this._children_objects.forEach(child => {
                child._parent_object = this;
                child._root_node.parent = this._root_node;
            });
        }
        this.set_parent(new_parent);
    }
    get parent() {
        return this._parent_object;
    }
    set_parent(new_parent) {
        // If we already had this parent (or no parent in the case of null) then do nothing
        if (this._parent_object === new_parent)
            return;
        const our_id = this._object_id;
        // Unparent from old parent and set our parent to null
        const old_parent = this._parent_object;
        if (old_parent !== null) {
            old_parent._child_removed_observable.notify(old_parent, this);
            const children = old_parent._children_objects;
            if (children === null) {
                console.error('Cannot remove child from object with no children');
                return; // TODO: Throw error?
            }
            if (children.length === 1) {
                old_parent._children_objects = null;
            }
            else {
                // Find us in the children list of the old parent
                const index = children.findIndex((obj) => { return obj.id === our_id; }); // TODO: This is expensive when deleting
                if (index === -1) {
                    console.error('Could not remove child from object since it is not the parent');
                }
                else {
                    children.splice(index, 1);
                }
            }
            this._parent_object = null;
            this._root_node.parent = null; // Remove babylon parent
        }
        if (new_parent !== null) {
            new_parent._child_added_observable.notify(new_parent, this);
            this._parent_object = new_parent;
            if (new_parent._children_objects === null) {
                new_parent._children_objects = []; // TODO: Set?
            }
            new_parent._children_objects.push(this);
            this._root_node.parent = new_parent._root_node; // Set new babylon parent
        }
        // we got a new parent for this object, so we need to recompute the world matrixes
        this.mark_world_matrices_to_be_recomputed();
    }
    dispose(delete_children, parent_is_disposing = false) {
        var _a;
        if (this._root_node.isDisposed())
            return;
        this.viewer.interactable_disposed_observable.notify(this);
        this._disposed_observable.notify(this._object_id);
        this._input_handler.dispose();
        this._changed_observable.dispose();
        this._disposed_observable.dispose();
        if (!parent_is_disposing) {
            this.set_parent(null);
        }
        // NOTE: We remove the babylon node here early so the hiarchy we are removing from become smaller
        this._root_node.parent = null;
        if (this._children_objects) {
            this._children_objects.forEach(child => {
                if (delete_children) {
                    child.dispose(true, true);
                }
                else {
                    child._parent_object = null; // No need to call set_parent since the old parent was this and this is going away
                    child._root_node.parent = null;
                    child.mark_world_matrices_to_be_recomputed();
                }
            });
            this._children_objects.length = 0;
            this._children_objects = null;
        }
        this._child_added_observable.dispose();
        this._child_removed_observable.dispose();
        this.viewer.get_input_handler_manager().remove_interactable(this);
        (_a = this._asset_instance) === null || _a === void 0 ? void 0 : _a.dispose();
        // Now our babylon _root_node has no parent and no children from other SceneObject that we want to keep. Dispose all babylon nodes
        this._root_node.dispose(false);
        this.viewer_scene.objects.remove(this.id);
    }
    get_scale() {
        return new BABYLON_Vector3().copyFrom(this._root_node.scaling);
    }
    set_scale(x, y, z) {
        const scale = this._root_node.scaling;
        if (scale.x === x && scale.y === y && scale.z === z)
            return;
        scale.set(x, y, z);
        this.mark_world_matrices_to_be_recomputed();
        this._changed_observable.notify(this, this);
        this.viewer.mark_scene_dirty();
    }
    get scale() {
        return this._root_node.scaling;
    }
    set scale(value) {
        this.set_scale(value.x, value.y, value.z);
    }
    get_rotation() {
        return new BABYLON_Vector3().copyFrom(this._root_node.rotation);
    }
    set_rotation(x, y, z) {
        const rotation = this._root_node.rotation;
        if (rotation.x === x && rotation.y === y && rotation.z === z)
            return;
        rotation.set(x, y, z);
        this.mark_world_matrices_to_be_recomputed();
        this._changed_observable.notify(this, this);
        this.viewer.mark_scene_dirty();
    }
    get rotation() {
        return this._root_node.rotation;
    }
    set rotation(value) {
        this.set_rotation(value.x, value.y, value.z);
    }
    get_position() {
        return new BABYLON_Vector3().copyFrom(this._root_node.position);
    }
    set_position(x, y, z) {
        const position = this._root_node.position;
        if (position.x === x && position.y === y && position.z === z)
            return;
        position.set(x, y, z);
        this.mark_world_matrices_to_be_recomputed();
        this._changed_observable.notify(this, this);
        this.viewer.mark_scene_dirty();
    }
    get position() {
        return this._root_node.position;
    }
    set position(value) {
        this.set_position(value.x, value.y, value.z);
    }
    set_outline_enabled(enabled, propagate_to_children) {
        this.children_iterator(!!propagate_to_children, true, (object) => {
            if (object._outline === enabled)
                return;
            object._outline = enabled;
            this.viewer.outline_layer.set_outline(object, object._outline);
            if (enabled) {
                // When enabling we need to set colors and width again, as the outline layer only keeps track of active outlines.
                // TODO: See IC3V-1569 with regards to applying outline properties on non-outline objects
                this.viewer.outline_layer.set_object_outline_color(object, object._outline_color);
                this.viewer.outline_layer.set_object_outline_width(object, object._outline_width);
            }
        });
    }
    is_outline_enabled() {
        return this._outline;
    }
    set_outline_block_enabled(enabled, propagate_to_children) {
        this.children_iterator(!!propagate_to_children, true, (object) => {
            if (object._outline_block === enabled)
                return;
            object._outline_block = enabled;
            this.viewer.outline_layer.set_outline_block(object, object._outline_block);
        });
    }
    is_outline_block_enabled() {
        return this._outline_block;
    }
    get outline_color() {
        var _a;
        return (_a = this._outline_color) !== null && _a !== void 0 ? _a : this.viewer.outline_layer.get_global_outline_color();
    }
    set outline_color(value) {
        if (this._outline_color === null && value === null)
            return;
        if (this._outline_color !== null &&
            value !== null &&
            value.r === this.outline_color.r &&
            value.g === this.outline_color.g &&
            value.b === this.outline_color.b &&
            value.a === this.outline_color.a)
            return;
        const babylon_color = value !== null ? new BABYLON_Color4(value.r, value.g, value.b, value.a) : null;
        this._outline_color = babylon_color;
        // TODO: See IC3V-1569 with regards to applying outline properties on non-outline objects
        if (this._outline) {
            this.viewer.outline_layer.set_object_outline_color(this, this._outline_color);
        }
    }
    get outline_width() {
        var _a;
        return (_a = this._outline_width) !== null && _a !== void 0 ? _a : this.viewer.outline_layer.get_global_outline_width();
    }
    set outline_width(value) {
        if (this._outline_width === value)
            return;
        this._outline_width = value;
        // TODO: See IC3V-1569 with regards to applying outline properties on non-outline objects
        if (this._outline) {
            this.viewer.outline_layer.set_object_outline_width(this, this._outline_width);
        }
    }
    mark_world_matrices_to_be_recomputed() {
        this.viewer.mark_world_bounds_dirty();
        this.children_iterator(true, true, (scene_object) => {
            scene_object._root_node.markAsDirty('');
            scene_object.mesh_iterator(true, (mesh) => {
                mesh.markAsDirty('');
            });
            this.viewer.light_system.on_object_modified(scene_object);
        });
    }
    traverse_nodes_internal(node, transform, block_child_objects, callback) {
        if (block_child_objects && this._children_objects !== null) {
            for (let j = 0; j < this._children_objects.length; j++) {
                if (node === this._children_objects[j]._root_node) {
                    return;
                }
            }
        }
        if (node instanceof BABYLON_TransformNode) {
            const new_transform = new BABYLON_Matrix();
            // We need compute the world matrix to make Babylon update its local matrix, otherwise we get incorrect results.
            node.computeWorldMatrix();
            node._localMatrix.multiplyToRef(transform, new_transform);
            transform = new_transform;
        }
        callback(node, transform);
        const children = node.getChildren();
        for (let i = 0; i < children.length; i++) {
            this.traverse_nodes_internal(children[i], transform, block_child_objects, callback);
        }
    }
    /**
     * Traverses the vertices in local space in this object. Does not traverse child objects.
     * @param callback The callback to process the vertex
     * @param initial_transform Initial transform (defaults to the identity). For example, if intial_transform is the objects world matrix, we'd get the vertices in world space.
     */
    traverse_vertices_local_space(callback, initial_transform = BABYLON_Matrix.Identity()) {
        // Temp vectors to cache
        const vertex = new BABYLON_Vector3();
        const transformed_vertex = new BABYLON_Vector4();
        // Skip the root node as it contains this object's "world transform".
        // We want to say in "local space" to our object.
        const children = this._root_node.getChildren();
        for (let i = 0; i < children.length; i++) {
            this.traverse_nodes_internal(children[i], initial_transform, true, (node, transform) => {
                if (node instanceof BABYLON_AbstractMesh) {
                    const mesh = node;
                    // This check is needed, otherwise Babylon gives an error in getPositionData when there are no vertices.
                    if (!mesh.isVerticesDataPresent(BABYLON_VertexBuffer.PositionKind))
                        return;
                    const data = mesh.getPositionData(true, true);
                    if (data !== null) {
                        for (let i = 0; i < data.length; i += 3) {
                            vertex.x = data[i + 0];
                            vertex.y = data[i + 1];
                            vertex.z = data[i + 2];
                            BABYLON_Vector4.TransformCoordinatesToRef(vertex, transform, transformed_vertex);
                            callback(transformed_vertex);
                        }
                    }
                }
            });
        }
    }
    get_world_matrix() {
        return this.root_node.getWorldMatrix();
    }
    get_local_matrix() {
        // We tell Babylon to compute the world matrix, as it computes the local matrix if it was out-of-date.
        this._root_node.computeWorldMatrix();
        return this.root_node._localMatrix;
    }
    // Fetches or calculates the object's bounding volume
    get_local_space_bounding_info() {
        if (this._local_bounding_info_calculation_promise !== null)
            return this._local_bounding_info_calculation_promise;
        this._local_bounding_info_calculation_promise = this.ready.then(() => {
            if (this._local_bounding_info !== null) {
                this._local_bounding_info_calculation_promise = null;
                return this._local_bounding_info;
            }
            else {
                const bounds = {
                    min: new BABYLON_Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE),
                    max: new BABYLON_Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE)
                };
                this.traverse_vertices_local_space((vertex) => {
                    IVector3.min_to_ref(bounds.min, vertex, bounds.min);
                    IVector3.max_to_ref(bounds.max, vertex, bounds.max);
                });
                const sphere_center = new BABYLON_Vector3();
                sphere_center.x = 0.5 * (bounds.min.x + bounds.max.x);
                sphere_center.y = 0.5 * (bounds.min.y + bounds.max.y);
                sphere_center.z = 0.5 * (bounds.min.z + bounds.max.z);
                let sphere_radius_squared = 0.0;
                const tmp_delta = new BABYLON_Vector3();
                this.traverse_vertices_local_space((vertex) => {
                    sphere_center.subtractFromFloatsToRef(vertex.x, vertex.y, vertex.z, tmp_delta);
                    sphere_radius_squared = Math.max(sphere_radius_squared, tmp_delta.lengthSquared());
                });
                const sphere = {
                    center: sphere_center,
                    radius: Math.sqrt(sphere_radius_squared)
                };
                const info = {
                    aabb: bounds,
                    sphere: sphere
                };
                this._local_bounding_info_calculation_promise = null;
                if (this.can_cache_local_bounds()) {
                    this._local_bounding_info = info;
                }
                return info;
            }
        });
        return this._local_bounding_info_calculation_promise;
    }
    // TODO: Once is_loaded becomes ready promise safe we should let the async endpoint just wrap this endpoint
    get_local_space_bounding_info_sync() {
        if (!this._has_bounds)
            throw Error('get_local_space_bounding_info_sync requires object to have loaded before calling');
        if (this._local_bounding_info !== null)
            return this._local_bounding_info;
        const bounds = {
            min: new BABYLON_Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE),
            max: new BABYLON_Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE)
        };
        this.traverse_vertices_local_space((vertex) => {
            IVector3.min_to_ref(bounds.min, vertex, bounds.min);
            IVector3.max_to_ref(bounds.max, vertex, bounds.max);
        });
        const sphere_center = new BABYLON_Vector3();
        sphere_center.x = 0.5 * (bounds.min.x + bounds.max.x);
        sphere_center.y = 0.5 * (bounds.min.y + bounds.max.y);
        sphere_center.z = 0.5 * (bounds.min.z + bounds.max.z);
        let sphere_radius_squared = 0.0;
        const tmp_delta = new BABYLON_Vector3();
        this.traverse_vertices_local_space((vertex) => {
            sphere_center.subtractFromFloatsToRef(vertex.x, vertex.y, vertex.z, tmp_delta);
            sphere_radius_squared = Math.max(sphere_radius_squared, tmp_delta.lengthSquared());
        });
        const sphere = {
            center: sphere_center,
            radius: Math.sqrt(sphere_radius_squared)
        };
        const info = {
            aabb: bounds,
            sphere: sphere
        };
        this._local_bounding_info_calculation_promise = null;
        if (this.can_cache_local_bounds()) {
            this._local_bounding_info = info;
        }
        return info;
    }
    transform_offset_with_world_matrix(offset) {
        const known_offset = offset ? new BABYLON_Vector3().fromIVector3(offset) : new BABYLON_Vector3();
        const world_matrix_transformed_point = BABYLON_Vector3.TransformCoordinatesToRef(known_offset, this._root_node.getWorldMatrix(), new BABYLON_Vector3());
        return world_matrix_transformed_point;
    }
    transform_direction_with_world_matrix(local_direction) {
        // Calculate local_dir to world dir
        const local_dir = new BABYLON_Vector3(local_direction.x, local_direction.y, local_direction.z);
        local_dir.normalize();
        return BABYLON_Vector3.TransformNormalToRef(local_dir, this._root_node.getWorldMatrix(), new BABYLON_Vector3());
    }
    recalculate_local_bounds() {
        this._local_bounding_info = null;
        if (this.can_cache_local_bounds()) {
            // Pre-heats the cache
            this.get_local_space_bounding_info();
        }
        this._changed_observable.notify(this, this);
    }
    get visible() {
        return this._visible;
    }
    set visible(v) {
        if (this._visible === v) {
            return;
        }
        this._visible = v;
        this.mesh_iterator(false, mesh => {
            mesh.isVisible = this._visible;
            mark_mesh_dirty_for_lightsystem(this.viewer.light_system, mesh);
        });
        this.changed_observable.notify(this, this);
    }
    get enabled() {
        return this._root_node.isEnabled();
    }
    set enabled(v) {
        const update_lights = v !== this.enabled;
        this._root_node.setEnabled(v);
        // Make sure to mark dirty for light system if we've switched between enabled/disabled
        if (update_lights) {
            this.mesh_iterator(true, mesh => {
                mark_mesh_dirty_for_lightsystem(this.viewer.light_system, mesh);
            });
        }
    }
    get receive_shadow() {
        return this._receive_shadow;
    }
    set receive_shadow(v) {
        if (this._asset_instance) {
            this._asset_instance.set_receive_shadows(v);
        }
        this._receive_shadow = v;
    }
    get alpha_index() {
        return this._alpha_index;
    }
    set alpha_index(v) {
        if (this._alpha_index === v)
            return;
        this._alpha_index = v;
        this.mesh_iterator(false, (mesh) => {
            mesh.alphaIndex = this._alpha_index;
        });
    }
    get on_drag_enable() {
        return this._on_drag_enable;
    }
    set on_drag_enable(enabled) {
        this._on_drag_enable = enabled;
    }
    get on_click_enable() {
        return this._on_click_enable;
    }
    set on_click_enable(enabled) {
        if (this._on_click_enable === enabled)
            return;
        this._on_click_enable = enabled;
    }
    get root() {
        return this._root_node;
    }
    set name(s) {
        this._root_node.name = this._root_node.id = s;
    }
    get name() {
        return this._root_node.name;
    }
    get drag_interactable() {
        return (this.drag_begin_observable.has_active_observers ||
            this.drag_observable.has_active_observers ||
            this.drag_end_observable.has_active_observers) &&
            this._on_drag_enable;
    }
    get click_interactable() {
        return this.click_observable.has_active_observers && this._on_click_enable;
    }
    get hover_interactable() {
        return this.pointer_enter_observable.has_active_observers || this.pointer_leave_observable.has_active_observers;
    }
    get has_input_handler() {
        return this.drag_interactable || this.click_interactable || this.hover_interactable;
    }
    update_albedo_roughness_factor_internal() {
        var _a;
        (_a = this._asset_instance) === null || _a === void 0 ? void 0 : _a.instance_iterator((instance) => {
            instance.set_color_roughness_factor(this._base_color);
        });
    }
    set_base_color(base_color) {
        const linear_color = IColor.to_linear_space(base_color);
        if (IVector3.equals(linear_color, this._base_color)) {
            return;
        }
        this._base_color.set(linear_color.x, linear_color.y, linear_color.z, this._base_color.w);
        this.changed_observable.notify(this, this);
        this.update_albedo_roughness_factor_internal();
    }
    set_roughness_factor(roughness_factor) {
        if (this._base_color.w === roughness_factor)
            return;
        this._base_color.w = roughness_factor;
        this.changed_observable.notify(this, this);
        this.update_albedo_roughness_factor_internal();
    }
    set_raycast_bounds(bounds) {
        let cloned_bounds = null;
        if (bounds !== null) {
            cloned_bounds = [];
            for (let i = 0; i < bounds.length; i++) {
                cloned_bounds.push(clone_bounds(bounds[i]));
            }
        }
        this._interaction_bounds = cloned_bounds;
        this.update_raycast_bounds_visualization();
    }
    get_raycast_bounds() {
        return this._interaction_bounds;
    }
    update_raycast_bounds_visualization() {
        if (this.bounds_meshes) {
            // Clean out old meshes
            for (const mesh of this.bounds_meshes) {
                mesh.dispose_instances();
                mesh.dispose();
            }
        }
        this.bounds_meshes.length = 0;
        if (this._show_interaction_bounds) {
            // Create new UiMeshes
            if (this._interaction_bounds) {
                for (const bound of this._interaction_bounds) {
                    const mesh = this.viewer.create_ui_mesh();
                    this.bounds_meshes.push(mesh);
                    let instance = null;
                    if (is_aabb(bound)) {
                        const center = aabb_center(bound);
                        const size = aabb_size(bound);
                        const positions = new Float32Array([
                            -0.5, -0.5, -0.5,
                            0.5, -0.5, -0.5,
                            -0.5, -0.5, 0.5,
                            0.5, -0.5, 0.5,
                            -0.5, 0.5, -0.5,
                            0.5, 0.5, -0.5,
                            -0.5, 0.5, 0.5,
                            0.5, 0.5, 0.5
                        ]);
                        const indices = new Uint16Array([
                            0, 3, 2,
                            0, 1, 3,
                            0, 4, 5,
                            0, 5, 1,
                            1, 5, 7,
                            1, 7, 3,
                            2, 6, 4,
                            2, 4, 0,
                            2, 3, 7,
                            2, 7, 6,
                            4, 7, 5,
                            4, 6, 7
                        ]);
                        const colors = new Float32Array(8 * 4);
                        colors.fill(1.0);
                        for (let i = 3; i < colors.length; i += 4) {
                            colors[i] = 0.4;
                        }
                        mesh.load_mesh({
                            positions: positions,
                            indices: indices,
                            colors: colors
                        });
                        instance = mesh.create_instance();
                        instance.position = center;
                        instance.scale = size;
                    }
                    else if (is_sphere(bound)) {
                        const positions = [];
                        const indices = [];
                        const colors = [];
                        const radius = 1.0;
                        const segments = 16;
                        for (let i = 0; i <= segments; i++) {
                            const longitude = i * Math.PI / segments;
                            for (let j = 0; j <= segments; j++) {
                                const latitude = j * 2 * Math.PI / segments;
                                const x = Math.sin(longitude) * Math.cos(latitude);
                                const y = Math.cos(longitude);
                                const z = Math.sin(longitude) * Math.sin(latitude);
                                positions.push(x * radius, y * radius, z * radius);
                                colors.push(1, 1, 1, 0.4);
                            }
                        }
                        for (let i = 0; i < segments; i++) {
                            for (let j = 0; j < segments; j++) {
                                const first = (i * (segments + 1)) + j;
                                const second = first + segments + 1;
                                indices.push(first, first + 1, second);
                                indices.push(second, first + 1, second + 1);
                            }
                        }
                        mesh.load_mesh({
                            positions: new Float32Array(positions),
                            indices: new Uint16Array(indices),
                            colors: new Float32Array(colors)
                        });
                        instance = mesh.create_instance();
                        instance.position = bound.position;
                        instance.scale = new IVector3(bound.radius, bound.radius, bound.radius);
                    }
                    assert(mesh, `failed to create bounds visualization for bound: ${JSON.stringify(bound)}`);
                    if (instance) {
                        instance.set_parent(this.id);
                        instance.color_factor = this._interaction_bounds_tint;
                    }
                }
            }
        }
        this.viewer.mark_scene_dirty();
    }
    // Returns true if it's OK to cache local bounds.
    can_cache_local_bounds() {
        // If we have skeletal animation, we need to recalculate our bounds each time.
        return !this._has_skeletal_animation;
    }
};
SceneObject = __decorate([
    Debug.Selectable({
        icon: '⊡',
        name: 'SceneObject',
        tooltip: (object) => `SceneObject: ${object.id}`,
        world_position: (object) => object.transform_offset_with_world_matrix(),
        is_enabled: false
    })
], SceneObject);
export { SceneObject };
//# sourceMappingURL=SceneObject.js.map